import type { BloomreachDoc } from "~/lib/bloomreach/types";

// Matches the JsonLdSchema branch of svelte-meta-tags' FlexibleSchema without importing the
// full schema-dts union (which is too complex for TypeScript to represent in $derived.by).
type JsonLdSchema = Record<string, unknown> & {
  "@type"?: string | readonly string[];
  "@context"?: string;
  "@id"?: string;
};

export type ItemListOrderValue =
  | "https://schema.org/ItemListOrderAscending"
  | "https://schema.org/ItemListOrderDescending"
  | "https://schema.org/ItemListUnordered";

export function itemListOrderFromSort(sort: string | undefined): ItemListOrderValue {
  if (sort?.endsWith(" asc")) return "https://schema.org/ItemListOrderAscending";
  if (sort?.endsWith(" desc")) return "https://schema.org/ItemListOrderDescending";
  return "https://schema.org/ItemListUnordered";
}

const AVAILABILITY_BY_STOCK_SUMMARY: Record<
  NonNullable<BloomreachDoc["stock_status_summary"]>,
  string
> = {
  in_stock: "https://schema.org/InStock",
  preorder: "https://schema.org/PreOrder",
  // Still purchasable via its pre-order variants.
  not_in_stock: "https://schema.org/PreOrder",
  out_of_stock: "https://schema.org/OutOfStock",
};

export function buildProductSubSchema(doc: BloomreachDoc, currency: string): JsonLdSchema {
  // Unknown stock omits availability rather than claiming InStock.
  const availability = doc.stock_status_summary
    ? AVAILABILITY_BY_STOCK_SUMMARY[doc.stock_status_summary]
    : undefined;
  return {
    "@type": "Product",
    name: doc.title,
    sku: doc.pid,
    ...(doc.thumb_image ? { image: doc.thumb_image } : {}),
    ...(doc.url ? { url: doc.url } : {}),
    brand: { "@type": "Brand", name: "DUSK" },
    ...(doc.review_rating !== undefined && doc.review_count !== undefined
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: doc.review_rating,
            reviewCount: doc.review_count,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
    offers: {
      "@type": "Offer",
      priceCurrency: currency,
      price: doc.sale_price ?? doc.price,
      ...(availability ? { availability } : {}),
    },
  };
}

function buildListItems(docs: BloomreachDoc[], currency: string): JsonLdSchema[] {
  return docs
    .filter((doc) => Boolean(doc.url))
    .map((doc, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: doc.url,
      item: buildProductSubSchema(doc, currency),
    }));
}

interface CollectionPageParams {
  name: string;
  description?: string;
  url: string;
  shopUrl: string;
  docs: BloomreachDoc[];
  activeSort: string;
  currency: string;
}

export function buildCollectionPageSchema({
  name,
  description,
  url,
  shopUrl,
  docs,
  activeSort,
  currency,
}: CollectionPageParams): JsonLdSchema {
  const itemListElement = buildListItems(docs, currency);

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    url,
    ...(description ? { description } : {}),
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: shopUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name,
          item: url,
        },
      ],
    },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: itemListElement.length,
      itemListOrder: itemListOrderFromSort(activeSort),
      itemListElement,
    },
  };
}

interface SearchResultsPageParams {
  searchQuery: string;
  url: string;
  docs: BloomreachDoc[];
  activeSort: string;
  currency: string;
}

export function buildSearchResultsPageSchema({
  searchQuery,
  url,
  docs,
  activeSort,
  currency,
}: SearchResultsPageParams): JsonLdSchema {
  const itemListElement = buildListItems(docs, currency);

  return {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    name: `Search results for "${searchQuery}"`,
    url,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: itemListElement.length,
      itemListOrder: itemListOrderFromSort(activeSort),
      itemListElement,
    },
  };
}
